import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";

const Pricing = () => {
  const [annual, setAnnual] = useState(true);
  const navigate = useNavigate();
  
  const plans = [
    {
      name: "Free",
      price: 0,
      description: "Perfect for individuals and small groups",
      features: [
        "Basic calendar functionality",
        "Up to 10 events per month",
        "Email notifications",
        "Mobile access",
        "7-day event history",
        "Up to 5 social circles"
      ],
      cta: "Sign Up",
      popular: false,
      color: "bg-gray-100"
    },
    {
      name: "Pro",
      price: annual ? 9.99 : 12.99,
      description: "Everything you need for advanced scheduling",
      features: [
        "Unlimited events",
        "Calendar sync with Google & iCloud",
        "Unlimited social circles",
        "Fine-grained privacy controls",
        "Custom event colors",
        "Unlimited event history",
        "Priority support",
        "Advanced event management"
      ],
      cta: "Get Pro",
      popular: true,
      color: "bg-blue-50"
    },
    {
      name: "Family",
      price: annual ? 14.99 : 19.99,
      description: "Share with your entire family",
      features: [
        "All Pro features",
        "Up to 6 family members",
        "Family sharing controls",
        "Shared family calendar",
        "Family event templates",
        "Premium support",
        "Family activity tracking"
      ],
      cta: "Get Family Plan",
      popular: false,
      color: "bg-green-50"
    }
  ];
  
  const handlePlanSelection = (planName: string) => {
    navigate(`/signup?plan=${planName.toLowerCase()}`);
  };
  
  return (
    <div className="min-h-screen bg-white">
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-6">Simple, Transparent Pricing</h1>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8">
              Choose the plan that's right for you. All plans include a 14-day free trial.
            </p>
            
            <div className="inline-flex items-center bg-gray-100 rounded-full p-1 mb-8">
              <Button
                variant={annual ? "default" : "ghost"}
                className={`rounded-full ${annual ? "bg-white shadow-sm" : ""}`}
                onClick={() => setAnnual(true)}
              >
                Annual (Save 20%)
              </Button>
              <Button
                variant={!annual ? "default" : "ghost"}
                className={`rounded-full ${!annual ? "bg-white shadow-sm" : ""}`}
                onClick={() => setAnnual(false)}
              >
                Monthly
              </Button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8 max-w-7xl mx-auto">
            {plans.map((plan, index) => (
              <Card
                key={index}
                className={`border ${plan.popular ? "border-blue-400 shadow-lg" : "border-gray-200"} relative overflow-hidden`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0">
                    <Badge className="rounded-tl-none rounded-tr-none rounded-br-none m-0 bg-blue-600">
                      Most Popular
                    </Badge>
                  </div>
                )}
                <div className={`h-2 w-full ${plan.popular ? "bg-blue-600" : plan.name === "Family" ? "bg-green-600" : "bg-gray-200"}`}></div>
                <CardHeader className={plan.color}>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <div className="mt-4 mb-2">
                    <span className="text-4xl font-bold">${plan.price}</span>
                    {plan.price > 0 && (
                      <span className="text-gray-600 ml-2">/{annual ? "mo, billed annually" : "month"}</span>
                    )}
                  </div>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button 
                    className={`w-full ${plan.popular ? "bg-blue-600 hover:bg-blue-700" : plan.name === "Family" ? "bg-green-600 hover:bg-green-700" : ""}`}
                    onClick={() => handlePlanSelection(plan.name)}
                  >
                    {plan.cta}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>
      
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-700">
              Everything you need to know about our pricing and plans.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">How does calendar syncing work?</h3>
                <p className="text-gray-700">
                  Pro and Family plans include two-way sync with Google Calendar and iCloud. This means events created in FyndTyme will appear in your other calendars, and vice versa.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Can I upgrade or downgrade my plan?</h3>
                <p className="text-gray-700">
                  Yes, you can change your plan at any time. If you upgrade, you'll be charged the prorated difference. 
                  If you downgrade, you'll receive credit toward your next billing cycle.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Do you offer a free trial?</h3>
                <p className="text-gray-700">
                  Yes, all paid plans come with a 14-day free trial. No credit card required until you decide to continue.
                </p>
              </div>
            </div>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">What's included in the Family plan?</h3>
                <p className="text-gray-700">
                  The Family plan includes all Pro features for up to 6 family members, plus family-specific features like shared family calendars and family activity tracking.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Can I cancel at any time?</h3>
                <p className="text-gray-700">
                  Absolutely. You can cancel your subscription at any time. There are no long-term contracts 
                  or cancellation fees.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">What payment methods do you accept?</h3>
                <p className="text-gray-700">
                  We accept all major credit cards and PayPal for subscription payments.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Ready to get started?</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
            Join thousands of users who are already making the most of their time with FyndTyme.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              size="lg"
              onClick={() => navigate("/signup")}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Start Your Free Trial
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate("/calendar")}
              className="border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              Try Demo Calendar
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
